/**
 * Case Studies Listing Page
 * Displays all available case studies
 */

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Sparkles, TrendingUp, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { PAGE_SEO_CONFIG } from '@/utils/seoUtils';
import { theOpulentCaseStudy } from '@/data/caseStudies/theOpulent';
import { ExtendedCaseStudy } from '@/types/caseStudy';

// Icon mapping for categories
const categoryIcons: { [key: string]: any } = {
  'fine-dining': Sparkles,
  'casual-dining': Users,
  'quick-service': Clock,
  'bar-restaurant': TrendingUp,
  'multi-location': MapPin
};

// All case studies - add new ones here
const allCaseStudies: ExtendedCaseStudy[] = [
  theOpulentCaseStudy,
  // Add more case studies as they're created
];

const CaseStudies = () => {
  const featuredCaseStudy = allCaseStudies.find(cs => cs.featured) || allCaseStudies[0];
  const otherCaseStudies = allCaseStudies.filter(cs => cs.id !== featuredCaseStudy?.id);

  return (
    <>
      <SEOHead
        title={PAGE_SEO_CONFIG.caseStudies.title}
        description={PAGE_SEO_CONFIG.caseStudies.description}
        keywords={PAGE_SEO_CONFIG.caseStudies.keywords?.join(', ')}
      />
      <Header />
      <div className="min-h-screen bg-[#2256e5] relative overflow-hidden pt-16">
      {/* Background elements for visual interest - same as How It Works */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-dineai-yellow/5 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 rounded-full bg-white/5 blur-2xl"></div>
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-12 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-6">
              <span className="text-dineai-yellow font-medium">Success Stories</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Real Restaurants, Real Results
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Discover how restaurants are transforming their operations with Dine AI
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Case Study with Restaurant Image */}
      {featuredCaseStudy && (
        <section className="pb-12 relative z-10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="max-w-5xl mx-auto"
            >
              {/* Glass morphic card with restaurant image */}
              <div className="rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20">
                {/* Restaurant Image Section */}
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img 
                    src="/opulent_lobby.png"
                    alt="The Opulent Restaurant Lobby"
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2256e5]/90 via-[#2256e5]/50 to-transparent"></div>
                  
                  {/* Overlay badges and title */}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-dineai-yellow text-black text-xs font-bold px-2 py-1 rounded">
                      FEATURED
                    </span>
                    <span className="text-white/80 text-sm bg-white/10 backdrop-blur-sm px-2 py-1 rounded">
                      {featuredCaseStudy.publishedDate}
                    </span>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4">
                    <h2 className="text-3xl font-bold text-white mb-2">
                      {featuredCaseStudy.restaurantName}
                    </h2>
                    <p className="text-lg text-white/90">
                      {featuredCaseStudy.heroSubtitle}
                    </p>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8">
                  {/* Key Metrics Preview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {featuredCaseStudy.keyMetrics.map((metric, index) => (
                      <div
                        key={index}
                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
                      >
                        <div className="text-2xl font-bold text-dineai-yellow">
                          {metric.value}
                        </div>
                        <div className="text-sm text-white/70">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {featuredCaseStudy.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full border border-white/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <Link to={`/case-studies/${featuredCaseStudy.slug}`}>
                    <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 rounded-full group">
                      Read Full Case Study
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Other Case Studies Grid */}
      {otherCaseStudies.length > 0 && (
        <section className="pb-12 relative z-10">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              More Success Stories
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {otherCaseStudies.map((caseStudy, index) => {
                const CategoryIcon = categoryIcons[caseStudy.category] || Sparkles;
                
                return (
                  <motion.div
                    key={caseStudy.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <CategoryIcon className="w-5 h-5 text-dineai-yellow" />
                      <span className="text-white/60 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {caseStudy.publishedDate}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-2">
                      {caseStudy.restaurantName}
                    </h3>
                    
                    <p className="text-white/70 text-sm mb-4 line-clamp-2">
                      {caseStudy.heroSubtitle}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {caseStudy.keyMetrics.slice(0, 2).map((metric, idx) => (
                        <div key={idx} className="bg-white/10 rounded px-2 py-1">
                          <span className="text-dineai-yellow font-bold text-sm">
                            {metric.value}
                          </span>
                          <span className="text-white/60 text-xs ml-1">
                            {metric.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    <Link 
                      to={`/case-studies/${caseStudy.slug}`}
                      className="text-dineai-yellow hover:text-yellow-300 text-sm font-medium flex items-center gap-1 group"
                    >
                      Read Case Study
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-12 bg-white/5 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-white/80 mb-8">
              Join The Opulent and other forward-thinking restaurants transforming their operations with Dine AI
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/restaurant-menu-search">
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 rounded-full text-lg py-6 px-8">
                  Start Your Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/dancing-queen">
                <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] rounded-full text-lg py-6 px-8 transform hover:scale-105 transition-all flex items-center gap-2">
                  WATCH DEMO
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    <Footer />
  </>
  );
};

export default CaseStudies;